import { AxiosResponse } from "axios";
import api from "@services/axios/apiManager";

/**
 * 帳戶帳單（瓦斯行開立的月結／瓦斯表帳單），對應 lpg-app-api `src/modules/bills`：
 * - 列表：GET /app-api/bills（需 X-Context）
 * - 明細：GET /app-api/bills/:unified_bill_id（需 X-Context）
 * 只回已開立的帳單；金額、合計、單價顯示與否都由後端照瓦斯行的帳單設定決定，前端照著畫。
 */

type ApiResult<T> = { state: "success"; data: T } | { state: "error"; error: unknown };

export type BillType = "order_bill" | "normal_device_bill";
export type BillStatus = "unpaid" | "partially_paid" | "paid";

export interface BillListItem {
  unified_bill_id: string;
  bill_type: BillType;
  status: BillStatus;
  supplier_name: string | null;
  /** 帳期（台灣日期 YYYY-MM-DD） */
  period_start: string;
  period_end: string;
  total_amount: number;
  paid_amount: number;
  /** 這張帳單還沒繳的金額（不含上期欠款） */
  outstanding_amount: number;
  /** 繳費期限 YYYY-MM-DD；瓦斯行沒開代收時為 null */
  payment_deadline: string | null;
  is_overdue: boolean;
}

export interface ListBillsRes {
  items: BillListItem[];
  next_cursor: string | null;
  summary: { unpaid_total: number; unpaid_count: number };
}

export interface BillOrderItem {
  kind: "gas" | "commodity" | "usage_fee";
  label: string;
  quantity: number;
  /** 瓦斯行設定不顯示單價時為 null */
  unit_price: number | null;
}

export interface BillOrderLine {
  order_id: string;
  /** 瓦斯行設定不顯示配送日時為 null */
  delivery_date: string | null;
  items: BillOrderItem[];
  refunds: { type: "存氣" | "退氣"; kilogram: number }[];
  floor_fee: number;
  /** true＝樓層費是當場付給配送員的，不在帳單金額裡 */
  floor_fee_paid_to_courier: boolean;
  amount: number;
}

export interface BillMeterLine {
  device_name: string;
  previous: { degree: number; date: string | null } | null;
  current: { degree: number; date: string | null } | null;
  usage: number;
  coefficient: number | null;
  unit_price: number | null;
  amount: number;
}

export interface BillTotals {
  subtotal: number;
  floor_fee: number | null;
  discount: number;
  paid: number;
  prior_owed: number | null;
  amount_due: number;
  outstanding: number;
}

export interface BillPaymentCodes {
  payment_deadline: string;
  amount: number;
  virtual_account: string | null;
  cvs_barcode: { barcode1: string; barcode2: string; barcode3: string } | null;
  cvs_unavailable_reason: "over_limit" | "expired" | "invalid_setting" | null;
}

export interface BillPaymentRecord {
  paid_at: string | null;
  amount: number;
  pay_ways: string[];
  /** 收款／付款（退給客戶）／沖抵 */
  direction: string;
}

export interface BillDetail extends BillListItem {
  supplier: { name: string | null; phone: string | null; address: string | null };
  /** 只有統編。後端刻意不回客戶名稱：瓦斯行客戶資料裡的名稱是瓦斯行內部稱呼 */
  customer: { tax_id_number: string | null };
  order_lines: BillOrderLine[];
  meter_lines: BillMeterLine[];
  totals: BillTotals;
  note: string | null;
  notices: string[];
  bank: { bank_name: string; account_name: string; account_number: string } | null;
  payment_codes: BillPaymentCodes | null;
  payments: BillPaymentRecord[];
  invoice: {
    invoice_number: string | null;
    invoice_time: string | null;
    random_number: string | null;
    has_allowance: boolean;
  } | null;
}

export const listBills = async ({
  jwtToken,
  xContext,
  params,
}: {
  jwtToken: string;
  xContext: string;
  params?: { limit?: number; cursor?: string };
}): Promise<ApiResult<ListBillsRes>> => {
  try {
    const res: AxiosResponse = await api.get("/app-api/bills", {
      params,
      headers: { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext },
    });
    return { state: "success", data: res.data?.data as ListBillsRes };
  } catch (error) {
    return { state: "error", error };
  }
};

export const getBill = async ({
  jwtToken,
  xContext,
  billId,
}: {
  jwtToken: string;
  xContext: string;
  billId: string;
}): Promise<ApiResult<BillDetail>> => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/bills/${encodeURIComponent(billId)}`, {
      headers: { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext },
    });
    return { state: "success", data: res.data?.data as BillDetail };
  } catch (error) {
    return { state: "error", error };
  }
};
